<script setup lang="ts">
import { storeToRefs } from "pinia";
import LxcError from "~/core/models/LxcError.class";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import { getArrayDelta } from "~/core/utils/array";
import { formatIsoDateToUtc } from "~/core/utils/date";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { versionCompare } from "~/core/utils/versionCompare";
import type { CampaignFunnelStep1 } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep1.vue";
import CampaignOperationMgrFunnelStep1 from "~/modules/campaign/components/CampaignOperationMgrFunnelStep1.vue";
import type { CampaignFunnelStep2 } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep2.vue";
import CampaignOperationMgrFunnelStep2 from "~/modules/campaign/components/CampaignOperationMgrFunnelStep2.vue";
import type { CampaignFunnelStep3 } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3.vue";
import CampaignOperationMgrFunnelStep3 from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3.vue";
import type { CampaignInclusionPeriodI } from "~/modules/campaign/components/CampaignOperationMgrFunnelStep3InclusionPeriods.vue";
import { CampaignCreationMode } from "~/modules/campaign/models/CampaignCreationMode.enum";
import { CampaignOMStatus } from "~/modules/campaign/models/CampaignOMStatus.enum";
import { CampaignOMType } from "~/modules/campaign/models/CampaignOMType.enum";
import { CUSTOM_INPUT_PARAMETERS_NAMES } from "~/modules/campaign/models/CampaignOperationMgr.enum";
import type { CampaignOperationMgr } from "~/modules/campaign/models/CampaignOperationMgr.interface";
import type { CampaignOperationMgrAddRemoveOperation } from "~/modules/campaign/models/CampaignOperationMgrAddRemoveOperation.interface";
import type { CampaignOperationMgrCreate } from "~/modules/campaign/models/CampaignOperationMgrCreate.interface";
import type { CampaignOperationMgrUpdate } from "~/modules/campaign/models/CampaignOperationMgrUpdate.interface";
import type { CampaignOperationParameter } from "~/modules/campaign/models/CampaignOperationParameter.interface";
import CampaignOperationMgrService from "~/modules/campaign/services/campaignOperationMgr.service";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { getCustomInputParameters } from "~/utils/getCustomInputParameters";
import ILxcAlertCircle from "~icons/lxc/alert-circle";
import ILxcArrowLeft from "~icons/lxc/arrow-left";

const props = defineProps<{
  campaign?: CampaignOperationMgr;
  show?: boolean;
}>();

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const emit = defineEmits(["update:show", "saved"]);

const step = ref(1);

// Possibility to edit the campaign on:
//   - creation
//   - or modification if the campaign is scheduled only
const canEditCampaign = computed(
  () => !props.campaign || props.campaign.status === CampaignOMStatus.SCHEDULED,
);

const campaignOperationMgrFunnelStep1: Ref<
  typeof CampaignOperationMgrFunnelStep1 | undefined
> = ref();
const campaignOperationMgrFunnelStep2: Ref<
  typeof CampaignOperationMgrFunnelStep2 | undefined
> = ref();
const campaignOperationMgrFunnelStep3: Ref<
  typeof CampaignOperationMgrFunnelStep3 | undefined
> = ref();

const isSubmitting = ref(false);

const campaignFunnelStep1: Ref<CampaignFunnelStep1> = ref({
  name: undefined,
  operationModelType: undefined,
  firmwareUuid: undefined,
});

const campaignFunnelStep2: Ref<CampaignFunnelStep2> = ref({
  dtwins: [],
  fleets: [],
});

const campaignFunnelStep3: Ref<CampaignFunnelStep3> = ref({
  dateRange: {
    startAt: undefined,
    endAt: undefined,
  },
  inclusionPeriods: {
    periods: [],
  },
});

const selectedFirmware: Ref<Firmware | undefined> = ref();
const showConfirm: Ref<boolean> = ref(false);

// reset campaign funnel step 1 from campaign
function resetCampaignFunnelStep1() {
  campaignFunnelStep1.value.name = props.campaign?.name;
  campaignFunnelStep1.value.operationModelType = undefined; // set to undefined, it will be set after all operation models loaded
  if (
    props.campaign?.operationParametersList &&
    props.campaign.operationParametersList.length > 0
  ) {
    campaignFunnelStep1.value.firmwareUuid = props.campaign
      .operationParametersList[0].customInputParameters?.firmwareUid as string;
  } else {
    campaignFunnelStep1.value.firmwareUuid = undefined;
  }
}

// reset campaign funnel step 3 from campaign
function resetCampaignFunnelStep3() {
  if (props.campaign?.launchPlannedAt && props.campaign?.expiredAt) {
    campaignFunnelStep3.value.dateRange.startAt = new Date(
      props.campaign.launchPlannedAt,
    );
    campaignFunnelStep3.value.dateRange.endAt = new Date(
      props.campaign.expiredAt,
    );
  } else {
    campaignFunnelStep3.value.dateRange.startAt = undefined;
    campaignFunnelStep3.value.dateRange.endAt = undefined;
  }

  if (props.campaign && props.campaign.operationParametersList) {
    const operationParameterWithInclusionPeriods =
      props.campaign.operationParametersList.find((operationParameter) => {
        if (operationParameter.customInputParameters) {
          return operationParameter.customInputParameters[
            CUSTOM_INPUT_PARAMETERS_NAMES.INCLUSION_PERIODS
          ];
        }
      });
    if (operationParameterWithInclusionPeriods) {
      campaignFunnelStep3.value.inclusionPeriods.periods =
        operationParameterWithInclusionPeriods?.customInputParameters
          ?.inclusionPeriods as CampaignInclusionPeriodI[];
    }
  } else {
    campaignFunnelStep3.value.inclusionPeriods.periods = [];
  }
}

const operationModels: Ref<OperationModel[]> = ref([]);

// set campaign type from campaign and all the operation models
function onAllOperationModelsLoaded(allOperationModels: OperationModel[]) {
  operationModels.value = allOperationModels;
  if (!campaignFunnelStep1.value.operationModelType) {
    // only inspect the first operation in order to retrieve the settings attributes
    // because only one operation model type is possible for now
    const operationModel = allOperationModels.find((operationModel) => {
      if (
        props.campaign?.operationParametersList &&
        props.campaign.operationParametersList.length > 0
      ) {
        return (
          operationModel.uid ===
          props.campaign?.operationParametersList[0]?.operationModelUid
        );
      } else {
        return false;
      }
    });
    campaignFunnelStep1.value.operationModelType = operationModel
      ? operationModel.type
      : undefined;
  }
}

async function next() {
  if (step.value === 1) {
    const isValid = await campaignOperationMgrFunnelStep1.value
      ?.validate()
      .catch(() => false);
    if (isValid) {
      step.value++;
    }
  } else if (step.value === 2) {
    const foundActionsInProgress = campaignFunnelStep2.value.dtwins.find(
      (dtwin) => dtwin.actionInProgress,
    );
    if (foundActionsInProgress) {
      showConfirm.value = true;
    } else {
      await step2();
    }
  }
}

async function step2() {
  const isValid = await campaignOperationMgrFunnelStep2.value
    ?.validate()
    .catch(() => false);
  if (isValid) {
    step.value++;
  }
}

function onConfirmContinueCreateUpdate() {
  showConfirm.value = false;
  step2();
}

async function getOperationParametersList(): Promise<
  CampaignOperationParameter[] | LxcError
> {
  const operationParametersList = [];

  // If selected firmware, filter the operation models device types on the selected firmware range
  const selectedOperationModels = operationModels.value
    .filter((operationModel) =>
      selectedFirmware.value?.range
        ? operationModel.deviceTypes.includes(selectedFirmware.value.range)
        : true,
    )
    // sort the operation models by version from the latest to the oldest
    .sort((operationModel1, operationModel2) =>
      versionCompare(operationModel2.version, operationModel1.version),
    );

  for (const operationModel of selectedOperationModels) {
    if (
      operationModel.type === campaignFunnelStep1.value.operationModelType &&
      currentOrganization.value?.code
    ) {
      const customInputParameters = await getCustomInputParameters(
        currentOrganization.value.code,
        operationModel,
        selectedFirmware.value,
        campaignFunnelStep3.value.inclusionPeriods,
      );

      if (LxcError.check(customInputParameters)) {
        return customInputParameters;
      }

      const operationParameters = {
        operationModelUid: operationModel.uid,
        customInputParameters,
      };

      operationParametersList.push(operationParameters);
    }
  }

  return operationParametersList;
}

async function getUpdateCampaignPayload(): Promise<
  CampaignOperationMgrUpdate | LxcError
> {
  const operationParametersList = await getOperationParametersList();
  if (LxcError.check(operationParametersList)) {
    return operationParametersList;
  }

  const campaignOperationMgrUpdate: CampaignOperationMgrUpdate = {
    compatibilityCriteria:
      campaignOperationMgrFunnelStep2.value?.compatibilityCriteria,
    operationParametersList,
  };

  const dtwinsDelta = getArrayDelta(
    campaignOperationMgrFunnelStep2.value?.initialSelectedDtwinsUid,
    campaignFunnelStep2.value.dtwins.map((dtwin) => dtwin.uid),
  );
  const fleetsDelta = getArrayDelta(
    campaignOperationMgrFunnelStep2.value?.initialSelectedFleetsUid,
    campaignFunnelStep2.value.fleets.map((fleet) => fleet.uid),
  );

  const deviceOperations: CampaignOperationMgrAddRemoveOperation = {
    add:
      dtwinsDelta.added.length > 0
        ? (dtwinsDelta.added as string[])
        : undefined,
    remove:
      dtwinsDelta.deleted.length > 0
        ? (dtwinsDelta.deleted as string[])
        : undefined,
  };

  const fleetOperations: CampaignOperationMgrAddRemoveOperation = {
    add:
      fleetsDelta.added.length > 0
        ? (fleetsDelta.added as string[])
        : undefined,
    remove:
      fleetsDelta.deleted.length > 0
        ? (fleetsDelta.deleted as string[])
        : undefined,
  };

  if (deviceOperations.add || deviceOperations.remove) {
    campaignOperationMgrUpdate.deviceOperations = deviceOperations;
  }

  if (fleetOperations.add || fleetOperations.remove) {
    campaignOperationMgrUpdate.fleetOperations = fleetOperations;
  }

  const launchPlannedAt =
    formatIsoDateToUtc(campaignFunnelStep3.value.dateRange.startAt) || "";
  if (launchPlannedAt !== props.campaign?.launchPlannedAt) {
    campaignOperationMgrUpdate.launchPlannedAt = launchPlannedAt;
  }

  const expiredAt =
    formatIsoDateToUtc(campaignFunnelStep3.value.dateRange.endAt) || "";
  if (expiredAt !== props.campaign?.expiredAt) {
    campaignOperationMgrUpdate.expiredAt = expiredAt;
  }

  return campaignOperationMgrUpdate;
}

async function getCreateCampaignPayload(): Promise<
  CampaignOperationMgrCreate | LxcError
> {
  const operationParametersList = await getOperationParametersList();

  if (LxcError.check(operationParametersList)) {
    return operationParametersList;
  }

  return {
    name: campaignFunnelStep1.value?.name || "",
    organizationCode: currentOrganization.value?.code || -1,
    deviceTwinUidList: campaignFunnelStep2.value.dtwins.map(
      (dtwin) => dtwin.uid,
    ),
    fleetUidList: campaignFunnelStep2.value.fleets.map((fleet) => fleet.uid),
    compatibilityCriteria:
      campaignOperationMgrFunnelStep2.value?.compatibilityCriteria,
    type: CampaignOMType.STATIC,
    creationMode: CampaignCreationMode.MANUAL,
    launchPlannedAt:
      formatIsoDateToUtc(campaignFunnelStep3.value.dateRange.startAt) || "",
    expiredAt:
      formatIsoDateToUtc(campaignFunnelStep3.value.dateRange.endAt) || "",
    operationParametersList,
  };
}

async function onSubmit() {
  const checkValidity: boolean[] = await campaignOperationMgrFunnelStep3.value
    ?.validate()
    .catch(() => false);
  if (!checkValidity.includes(false)) {
    isSubmitting.value = true;

    // update campaign
    if (props.campaign) {
      const campaign = await getUpdateCampaignPayload();

      if (LxcError.check(campaign)) {
        campaign.notify(NotificationKey.saveError);
      } else {
        const response = await CampaignOperationMgrService.update(
          props.campaign.uid,
          campaign,
          currentOrganization.value?.code,
        );

        if (LxcError.check(response)) {
          response.notify(NotificationKey.saveError);
        } else {
          showNotificationSuccess(t(NotificationKey.saveSuccess));
          emit("saved", props.campaign.uid);
        }
      }
    }
    // create campaign
    else {
      const campaign = await getCreateCampaignPayload();

      if (LxcError.check(campaign)) {
        campaign.notify(NotificationKey.saveError);
      } else {
        const response = await CampaignOperationMgrService.create(
          campaign,
          currentOrganization.value?.code,
        );

        if (LxcError.check(response)) {
          (response as LxcError).notify(NotificationKey.saveError);
        } else {
          showNotificationSuccess(t(NotificationKey.saveSuccess));
          emit("saved", response.uid);
        }
      }
    }

    isSubmitting.value = false;
  }
}

async function resetCampaign() {
  resetCampaignFunnelStep1();
  resetCampaignFunnelStep3();
}

// when showing the funnel:
// - reset the step to first one
// - reset campaign form
watch(
  () => props.show,
  () => {
    step.value = 1;
    resetCampaign();
  },
);

const resetDtwinsListAndInclusionPeriods = () => {
  // reset the dtwins list
  campaignFunnelStep2.value.dtwins = [];
  campaignFunnelStep2.value.fleets = [];

  // reset the inclusion periods
  if (
    campaignFunnelStep1.value.operationModelType &&
    !props.campaign?.operationTypes.includes(
      campaignFunnelStep1.value.operationModelType,
    )
  ) {
    campaignFunnelStep3.value.inclusionPeriods.periods = [];
  }
};

// when the campaign changed => reset the campaign form
watch(() => props.campaign, resetCampaign);

// when the operation model type changed
watch(
  () => campaignFunnelStep1.value.operationModelType,
  () => resetDtwinsListAndInclusionPeriods(),
);
// when the firmware changed
watch(
  () => campaignFunnelStep1.value.firmwareUuid,
  () => resetDtwinsListAndInclusionPeriods(),
);
</script>

<template>
  <lxc-common-modal
    :show="show"
    is-full-screen
    with-steps
    :step="step"
    :number-of-steps="3"
    @close="$emit('update:show', false)"
  >
    <template #header>
      <h2 v-show="step === 1" class="text-gray-900">
        {{
          campaign
            ? t("campaign.funnel.updateCampaign")
            : t("campaign.funnel.newCampaign")
        }}
      </h2>
      <h2 v-show="step === 2" class="text-gray-900">
        {{ t("campaign.funnel.deviceFleetSelection.title") }}
      </h2>
      <h2 v-show="step === 3" class="text-gray-900">
        {{ t("campaign.funnel.scheduling") }}
      </h2>
      <lxc-button
        v-if="step > 1"
        :title="t('campaign.funnel.previous')"
        type="tertiary"
        class="w-11 h-11 absolute top-1/2 -translate-y-1/2 left-8"
        @click="step--"
      >
        <i-lxc-arrow-left class="text-gray-700" height="20" width="20" />
      </lxc-button>
    </template>

    <template #body>
      <div v-show="step === 1" class="mt-7">
        <campaign-operation-mgr-funnel-step1
          ref="campaignOperationMgrFunnelStep1"
          v-model="campaignFunnelStep1"
          :is-editing="!!props.campaign"
          :can-edit="canEditCampaign"
          @select-firmware="selectedFirmware = $event"
          @all-operation-models-loaded="onAllOperationModelsLoaded"
        />
      </div>
      <div v-show="step === 2" class="mt-3">
        <campaign-operation-mgr-funnel-step2
          ref="campaignOperationMgrFunnelStep2"
          v-model="campaignFunnelStep2"
          :operation-models="operationModels"
          :operation-model-type="campaignFunnelStep1.operationModelType"
          :firmware="selectedFirmware"
          :fleet-uid="campaign?.fleetUid"
          :campaign-status="campaign?.status"
        />
      </div>
      <div v-show="step === 3">
        <campaign-operation-mgr-funnel-step3
          ref="campaignOperationMgrFunnelStep3"
          v-model="campaignFunnelStep3"
          :can-edit="canEditCampaign"
          :operation-models="operationModels"
          :operation-model-type="campaignFunnelStep1.operationModelType"
          :firmware="selectedFirmware"
        />
      </div>
    </template>

    <template #footer>
      <lxc-button
        v-if="step < 3"
        :title="t('campaign.funnel.next')"
        type="primary"
        @click="next"
      >
        {{ t("campaign.funnel.next") }}
      </lxc-button>
      <lxc-button
        v-if="step === 3"
        :title="
          campaign
            ? t('campaign.funnel.updateCampaign')
            : t('campaign.funnel.publish')
        "
        type="primary"
        :is-loading="isSubmitting"
        @click="onSubmit"
      >
        {{
          campaign
            ? t("campaign.funnel.updateCampaign")
            : t("campaign.funnel.publish")
        }}
      </lxc-button>
    </template>
  </lxc-common-modal>

  <confirm-modal
    :is-dialog-visible="showConfirm"
    :title="t('campaign.funnel.fleetSelection.actions.scheduled.confirm.title')"
    :description="
      t('campaign.funnel.fleetSelection.actions.scheduled.confirm.message')
    "
    :ok-label="
      t('campaign.funnel.fleetSelection.actions.scheduled.confirm.yes')
    "
    :cancel-label="
      t('campaign.funnel.fleetSelection.actions.scheduled.confirm.no')
    "
    :icon="ILxcAlertCircle"
    icon-color-theme="warning"
    @confirm="onConfirmContinueCreateUpdate"
    @cancel="showConfirm = false"
  />
</template>
